/**
 * O GRÁFICO DO OXIGÊNIO — «não estou vendo de onde isso vem».
 *
 * Pedido do gestor em 22/09/2026, e a frase dele é a especificação:
 *
 *   «fazer a leitura de oxigênio e receber a notícia de que o oxigênio está
 *    caindo ou estável ou subindo, mas sem olhar para os dados anteriores é
 *    algo que fica 'tenho que acreditar mas não estou vendo de onde isso vem'»
 *
 * É o mesmo defeito do trato repetido do T-8 e da anulação invisível: o app
 * afirma e não mostra a origem. Aqui a correção não é um aviso — é a série.
 *
 * ── O QUE FAZ ESTE GRÁFICO NÃO SER ENFEITE ────────────────────────────────
 *
 * As BANDAS. Ele não mostra só a linha: mostra as faixas que produziram a
 * recomendação — abaixo de 2, de 2 a 3. Com elas, «80% hoje» deixa de ser uma
 * afirmação do app e vira uma coisa que se vê: o ponto está dentro da faixa
 * ocre. Sem elas, seria um desenho bonito que não responde nada.
 *
 * As faixas vêm da tabela do banco, nunca daqui — outra piscicultura tem outra
 * água (regra 10).
 *
 * ── SVG NA MÃO, E NÃO UMA BIBLIOTECA ──────────────────────────────────────
 *
 * São vinte linhas de polilinha e retângulo. Uma biblioteca de gráfico custaria
 * mais quilobytes que o app inteiro de hoje, num celular que abre na beira do
 * tanque com sinal ruim — e traria uma dependência para o «o build enferruja»,
 * que é o ponto fraco 6 do projeto.
 *
 * As contas moram em `src/dominio/serie.ts`, com teste. Aqui só há desenho.
 */

import { useState } from 'react'
import type { FaixaDeCoeficiente } from '../acesso/api'
import { montarSerie, type LeituraDaSerie } from '../dominio/serie'
import { numero } from '../dominio/unidades'

/** A caixa do desenho, em unidades de SVG. O CSS estica para a largura real. */
const LARGURA = 300
const ALTURA = 120
/** Margem para o rótulo do eixo não encostar na borda. */
const ESQUERDA = 22
const BAIXO = 14
const TOPO = 6

export function GraficoDeOxigenio({
  leituras, faixas, maximoDeDias = 14,
}: {
  leituras: readonly LeituraDaSerie[]
  faixas: readonly FaixaDeCoeficiente[]
  maximoDeDias?: number
}) {
  const [aberto, setAberto] = useState(false)
  const serie = montarSerie(leituras, faixas, maximoDeDias)

  // O BOTÃO NÃO EXISTE SEM SÉRIE — regra que o gestor deu junto com o pedido.
  // Piscicultura sem oxímetro não vê gráfico vazio: desenho sem dado promete
  // uma informação que não existe, e some com o espaço de quem precisa do
  // resto da ficha.
  if (serie === null) return null

  const dentro = { largura: LARGURA - ESQUERDA, altura: ALTURA - BAIXO - TOPO }
  const emX = (x: number) => ESQUERDA + x * dentro.largura
  const emY = (y: number) => TOPO + y * dentro.altura

  const linha = serie.pontos.map((p) => `${emX(p.x)},${emY(p.y)}`).join(' ')
  const ultimo = serie.pontos[serie.pontos.length - 1]

  return (
    <div className="grafico-oxigenio">
      <button
        type="button"
        className="discreto"
        onClick={() => setAberto(!aberto)}
        aria-expanded={aberto}
      >
        {/* DIAS, e não leituras. Depois que o gráfico passou a usar só a
            primeira medição de cada dia, «14 leituras» passaria a contar uma
            coisa e mostrar outra — e num dia de duas medições o número não
            bateria com o que a Ronda lista logo acima. */}
        {aberto
          ? 'esconder o gráfico'
          : `ver os últimos ${serie.pontos.length} ${serie.pontos.length === 1 ? 'dia' : 'dias'}`}
      </button>

      {aberto && (
        <>
          <svg
            viewBox={`0 0 ${LARGURA} ${ALTURA}`}
            className="grafico-svg"
            role="img"
            aria-label={
              `Oxigênio de ${serie.primeiro} a ${serie.ultimo}: ` +
              serie.pontos.map((p) => `${numero(p.valor, 1)}`).join(', ') + ' mg/L'
            }
          >
            {/* AS BANDAS PRIMEIRO, para a linha passar por cima delas. */}
            {serie.bandas.map((b) => (
              <rect
                key={`${b.de}-${b.ate}`}
                x={ESQUERDA}
                y={emY(b.y)}
                width={dentro.largura}
                height={b.altura * dentro.altura}
                className={b.coeficiente <= 0.5 ? 'banda-corte' : 'banda-aviso'}
              />
            ))}

            {/* As marcas do eixo: são as das faixas, que é o que se procura. */}
            {serie.marcas.map((m) => {
              const y = emY(1 - m / serie.teto)
              return (
                <g key={m}>
                  <line
                    x1={ESQUERDA} y1={y} x2={LARGURA} y2={y}
                    className="grafico-regua"
                  />
                  <text x={ESQUERDA - 4} y={y + 3} className="grafico-marca">
                    {numero(m, 0)}
                  </text>
                </g>
              )
            })}

            <polyline points={linha} className="grafico-linha" />

            {serie.pontos.map((p, i) => (
              <circle
                key={`${p.data}-${i}`}
                cx={emX(p.x)}
                cy={emY(p.y)}
                r={i === serie.pontos.length - 1 ? 4 : 2.5}
                className={
                  i === serie.pontos.length - 1 ? 'grafico-ponto ultimo' : 'grafico-ponto'
                }
              />
            ))}
          </svg>

          <div className="grafico-pes">
            <span className="secundario">{diaCurto(serie.primeiro)}</span>
            {/* O rodapé já tem as duas datas nas pontas: repetir «14 dias» no
                meio seria dizer de novo o que se vê. O que ele não diz, e
                precisa dizer, é QUAL leitura do dia está no desenho. */}
            <span className="secundario">mg/L · 1ª do dia</span>
            <span className="secundario">
              {diaCurto(serie.ultimo)}
              {ultimo && <strong> · {numero(ultimo.valor, 1)}</strong>}
            </span>
          </div>

          {/* A LEGENDA DIZ O QUE A COR SIGNIFICA, em quilo de ração e não em
              coeficiente. «0,8» não é uma coisa que alguém veja no tanque. */}
          {serie.bandas.length > 0 && (
            <ul className="grafico-legenda">
              {serie.bandas.map((b) => (
                <li key={`${b.de}-${b.ate}`}>
                  <span
                    className={b.coeficiente <= 0.5 ? 'amostra-corte' : 'amostra-aviso'}
                    aria-hidden="true"
                  />
                  {numero(b.de, 0)} a {numero(b.ate, 0)} mg/L — o app oferece{' '}
                  {numero(b.coeficiente * 100, 0)}% do previsto
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  )
}

/** «18/09» — o suficiente para saber de quando é. */
function diaCurto(iso: string): string {
  const [, m, d] = iso.slice(0, 10).split('-')
  return `${d}/${m}`
}
